/**
 * Customer-facing legal and policy copy.
 *
 * Migrated out of `prototype/index.html` (renderLegalTerms / renderLegalPrivacy /
 * renderLegalAML / renderLegalResponsiblePlay / renderLegalCookies /
 * renderLegalRefunds / renderLegalComplaints / renderHowItWorks), which wrote these
 * documents as JS template strings behind a hand-rolled `goLegal()` navigator.
 *
 * THIS FILE IS DELIBERATELY PURE — no React, no network, no clock. That is what lets
 * `content.test.ts` run under the web workspace's `environment: 'node'` vitest config
 * (jsdom / happy-dom / @testing-library are not installed and installing them to
 * assert on prose would be the wrong fix). The same pattern as
 * `features/admin/logic.ts`.
 *
 * THREE RULES THE TESTS PIN, each because breaking it publishes a false promise:
 *
 * 1. NO "BET CREDIT" ANYWHERE. The prototype copy was written against v0.9, which had
 *    three balances. The product is v0.11: two balances (locked Savings + playable,
 *    withdrawable Play Balance), Bet Credit REMOVED. Copy describing a third
 *    promotional balance describes a product the customer cannot be given.
 *
 * 2. NO HARD-CODED RESPONSIBLE-GAMBLING DURATIONS. The prototype printed "7-day
 *    waiting period" and "minimum of 6 months". Those figures are becoming audit-logged
 *    `admin_params` an operator owns, read by the client from `/me` public params. A
 *    page that prints a number the backend is free to change is a promise we may not
 *    keep — so these documents describe the MECHANISM (decreases immediate, increases
 *    delayed, no shortening inside an active window) and the app states the live
 *    figures at the point the customer sets a limit.
 *
 * 3. NO STALE INFRASTRUCTURE OR INTERNAL REFERENCES. The prototype claimed production
 *    data would live in "AWS Sydney", named "Cuscal PayTo" as the payment provider,
 *    described the withdrawal rail as PayTo, and leaked "§16.5 of our internal spec"
 *    into a customer-facing privacy policy. Production Postgres is DigitalOcean SYD1;
 *    the payment provider is Zepto; internal section numbers mean nothing to a reader.
 */

export type LegalSlug =
  | 'terms'
  | 'privacy'
  | 'aml'
  | 'responsible-play'
  | 'cookies'
  | 'refunds'
  | 'complaints'
  | 'how-we-make-money';

export interface LegalSection {
  /** Section heading as shown to the customer. */
  heading: string;
  /** One or more paragraphs. Plain text — no HTML, no markdown. */
  body: string[];
  /** Optional bullet list rendered under the paragraphs. */
  list?: string[];
  /**
   * Marks a section as still pending legal review. Rendered as a DRAFT chip, the same
   * signal the prototype carried — a reader must be able to tell reviewed copy from
   * unreviewed copy.
   */
  draft?: boolean;
}

export interface LegalDoc {
  slug: LegalSlug;
  /** Page title, and the label used on the footer link. */
  title: string;
  /** One-line description shown under the title and on the index. */
  sub: string;
  /** Small category label above the title. */
  eyebrow: string;
  /** Lead paragraph, set apart from the numbered sections. */
  hero: string;
  sections: LegalSection[];
}

/**
 * Version stamp shown on every page. Bump this whenever copy changes materially — a
 * customer disputing a term needs to be able to say which version they agreed to.
 */
export const LEGAL_VERSION = 'v0.11 draft · 17 September 2026';

/** Shown beside the version. Sections marked DRAFT are pending legal review. */
export const LEGAL_REVIEW_NOTE =
  'Sections marked DRAFT are pending legal review and may change before launch.';

const TERMS: LegalDoc = {
  slug: 'terms',
  title: 'Terms & Conditions',
  sub: 'The agreement between you and Puntsave.',
  eyebrow: 'Legal',
  hero:
    'These terms govern your Puntsave account. The short version: your deposits stay yours. ' +
    'Bets move your money between two buckets you own — locked Savings and your withdrawable ' +
    'Play Balance — and no real money leaves your account because of a bet result. The only ' +
    'things that reduce the money we hold for you are the transaction fee and your own withdrawals.',
  sections: [
    {
      heading: '1. Who we are',
      body: [
        'Puntsave Pty Ltd (ABN to be confirmed) operates puntsave.com.au and the Puntsave web ' +
          'application. We are an Australian company and these terms are governed by the laws of ' +
          'Australia.',
        'Contact: support@puntsave.com.au.',
      ],
      draft: true,
    },
    {
      heading: '2. Eligibility',
      body: [
        'You must be at least 18 years old and an Australian resident to hold a Puntsave account. ' +
          'One account per person. You must provide accurate identity details and keep them current.',
        'We verify your identity before your first deposit. Accounts that cannot be verified cannot ' +
          'deposit, bet or withdraw.',
      ],
    },
    {
      heading: '3. Your two balances',
      body: [
        'Puntsave holds your money in two buckets, both of which are yours:',
      ],
      list: [
        'Savings (locked) — real money set aside for a lock term. It is not directly withdrawable ' +
          'until it unlocks.',
        'Play Balance — real money you can bet with and withdraw, subject to the withdrawal cap in ' +
          'section 4.',
      ],
    },
    {
      heading: '4. How deposits are split',
      body: [
        'Your first deposit is split between Savings and Play Balance at the recommended split rate ' +
          'shown to you at the time of deposit. Later deposits go to your Play Balance unless you ' +
          'choose otherwise.',
        'The split rate is a published product setting. We may change it, and the rate in force is ' +
          'always the one shown to you before you confirm a deposit.',
      ],
    },
    {
      heading: '5. How bets move your money',
      body: [
        'A bet never sends your money to us or to anyone else. It moves money between your own two ' +
          'buckets:',
      ],
      list: [
        'You place a bet from your Play Balance. The stake leaves Play Balance while the bet is open.',
        'If the bet wins, the payout is credited to your Play Balance, and an equal amount of your ' +
          'profit unlocks out of Savings — so a win makes more of your money withdrawable.',
        'If the bet loses, the stake moves into your locked Savings instead of being lost. This is ' +
          'the point of the product: money you would otherwise have lost is kept.',
        'If the bet is voided, the stake returns to your Play Balance.',
      ],
    },
    {
      heading: '6. The transaction fee',
      body: [
        'We charge a flat 4% transaction fee on each real-money bet, capped at $20 per calendar ' +
          'month (Australia/Brisbane time). It is charged on top of your stake at the moment you ' +
          'place the bet — a $20 bet costs $20.80 in total — and is rounded down to the cent. Once ' +
          'your fees for the month reach the cap, further bets that month carry no fee. A multi is ' +
          'one bet: it pays one fee on its stake, not one per leg. If you place no bets, you pay nothing.',
        'The fee is how Puntsave makes money and it is our only revenue from your account. It is ' +
          'not refunded when a bet loses, when you or we void a bet, or when a bet is settled again ' +
          'after a correction. The one exception: if we void a bet because the market itself was ' +
          'abandoned by the provider, your stake and the fee are both returned. We show your running ' +
          'total of fees paid, and how much of the monthly cap is used, on your activity statement.',
      ],
    },
    {
      heading: '7. Withdrawals',
      body: [
        'You can withdraw up to your withdrawable cap at any time. The cap is your total deposits, ' +
          'minus the transaction fees you have paid, minus everything you have already withdrawn or ' +
          'have a withdrawal pending for.',
        'Because winnings move money between your own buckets rather than adding new money, the cap ' +
          'does not grow when you win. It is a floor that protects the money you actually put in.',
        'Withdrawals are paid to a bank account in your own name and are subject to a cooling-off ' +
          'window — see Refunds & Withdrawals.',
      ],
    },
    {
      heading: '8. Payouts that exceed your Savings',
      body: [
        'If a bet wins and your profit is larger than the amount sitting in Savings, your Play ' +
          'Balance is still credited with the full payout. Savings simply unlocks down to zero.',
        'When that happens your Play Balance can be larger than your withdrawable cap. The extra is ' +
          'real to bet with but is not withdrawable, because it was never money you deposited. Your ' +
          'withdrawal cap stays at deposits minus fees minus withdrawals.',
      ],
    },
    {
      heading: '9. Fair use',
      body: [
        'You may not use automated systems to place bets, open more than one account, use another ' +
          'person’s identity or payment details, or attempt to exploit pricing or settlement ' +
          'errors. We may suspend or close an account that does.',
        'We may refuse or void a bet that was accepted at a price that was clearly wrong, or on an ' +
          'event that was abandoned or that had already started.',
      ],
      draft: true,
    },
    {
      heading: '10. Suspension and closure',
      body: [
        'You can close your account at any time. On closure we return your withdrawable balance to ' +
          'your verified bank account, and your locked Savings once it unlocks or by agreement.',
        'We may suspend or close an account for breach of these terms, for a compliance obligation, ' +
          'or where we reasonably believe an account is being used fraudulently.',
      ],
      draft: true,
    },
    {
      heading: '11. Liability',
      body: [
        'Nothing in these terms excludes any right you have under the Australian Consumer Law. ' +
          'Subject to that, we are not liable for indirect or consequential loss, or for loss of ' +
          'opportunity arising from a bet you did or did not place.',
      ],
      draft: true,
    },
    {
      heading: '12. Changes',
      body: [
        'We may update these terms. Material changes are notified in the app before they take ' +
          'effect. The version stamp at the top of this page identifies the version you are reading.',
      ],
    },
  ],
};

const PRIVACY: LegalDoc = {
  slug: 'privacy',
  title: 'Privacy Policy',
  sub: 'What we collect, why we collect it, and your rights under the Privacy Act.',
  eyebrow: 'Legal',
  hero:
    'We collect what we need to run your account, verify who you are, and meet our legal ' +
    'obligations — and not more. We do not sell your personal information, and we do not run ' +
    'advertising against it.',
  sections: [
    {
      heading: '1. What we collect',
      body: ['We collect:'],
      list: [
        'Identity details — name, date of birth, residential address, and the identity documents ' +
          'used to verify you.',
        'Contact details — email address, and phone number if you give us one.',
        'Financial details — the bank account used for deposits and withdrawals, and the record of ' +
          'every deposit, withdrawal, bet, settlement and fee on your account.',
        'Technical details — device and browser information, IP address, and the app settings saved ' +
          'in your browser.',
      ],
    },
    {
      heading: '2. Why we collect it',
      body: [
        'To open and operate your account, to hold and move money between your Savings and Play ' +
          'Balance, to process deposits and withdrawals, to verify your identity, to detect fraud ' +
          'and misuse, to provide responsible-gambling tools, and to meet our obligations under ' +
          'Australian anti-money-laundering and consumer law.',
      ],
    },
    {
      heading: '3. Fraud and misuse checks',
      body: [
        'We run automated checks on new accounts, deposits and withdrawals to detect duplicate ' +
          'accounts, shared payment instruments and other patterns that indicate misuse. Where a ' +
          'check flags an account, a person reviews it before any action is taken.',
        'You can ask us to explain a decision that affected your account, and to have it reviewed.',
      ],
    },
    {
      heading: '4. Who we share it with',
      body: ['We share personal information only with the providers we need to run the service:'],
      list: [
        'Zepto — our Australian payments provider, for deposits and withdrawals.',
        'Our identity verification provider, to confirm who you are before your first deposit.',
        'Our sports data providers, which receive no personal information — only anonymous market ' +
          'and results requests.',
        'Our email provider, to send account, security and withdrawal notifications.',
        'Regulators and law enforcement, where we are legally required to.',
      ],
    },
    {
      heading: '5. Where your data is held',
      body: [
        'Your account data is held in an Australian data centre — our production database runs in ' +
          'DigitalOcean’s Sydney (SYD1) region, for Australian data residency.',
        'Some of our service providers process limited data outside Australia. Where that happens we ' +
          'require contractual protections at least equivalent to the Australian Privacy Principles.',
      ],
    },
    {
      heading: '6. How long we keep it',
      body: [
        'We keep account, transaction and identity records for seven years after your account ' +
          'closes, as required by Australian anti-money-laundering law. Technical logs are kept for ' +
          'a much shorter period and are then deleted.',
      ],
      draft: true,
    },
    {
      heading: '7. Your rights',
      body: [
        'You can ask for a copy of the personal information we hold about you, ask us to correct it ' +
          'if it is wrong, and complain if you think we have mishandled it. Email ' +
          'privacy@puntsave.com.au.',
        'If you are not satisfied with our response you can contact the Office of the Australian ' +
          'Information Commissioner at oaic.gov.au or 1300 363 992.',
      ],
    },
    {
      heading: '8. Marketing',
      body: [
        'Marketing email is off by default. If you turn it on you can turn it off again at any time ' +
          'from your notification settings or by using the unsubscribe link. Security and account ' +
          'notices are not marketing and cannot be turned off.',
      ],
    },
  ],
};

const AML: LegalDoc = {
  slug: 'aml',
  title: 'AML/CT/KYC Policy',
  sub: 'How we identify customers and meet our anti-money-laundering obligations.',
  eyebrow: 'Compliance',
  hero:
    'Puntsave verifies every customer before their first deposit, monitors accounts for unusual ' +
    'activity, and reports what the law requires us to report. This page describes what that means ' +
    'for you.',
  sections: [
    {
      heading: '1. Identity verification',
      body: [
        'We verify your identity before you can deposit — not at your first withdrawal. Verification ' +
          'uses an accredited Australian identity provider and normally completes in under a minute.',
        'If electronic verification cannot confirm your identity we may ask you for documents. Until ' +
          'verification is complete you cannot deposit, bet or withdraw.',
      ],
    },
    {
      heading: '2. Source of funds',
      body: [
        'Deposits must come from an account in your own name. We do not accept deposits from third ' +
          'parties, and withdrawals are only ever paid to an account in your own name.',
        'For larger or unusual deposits we may ask you about the source of the funds before ' +
          'processing them.',
      ],
    },
    {
      heading: '3. Ongoing monitoring',
      body: [
        'We monitor accounts for patterns associated with money laundering, structuring, and account ' +
          'takeover. Where something looks wrong we may pause an account while we review it.',
      ],
    },
    {
      heading: '4. Reporting',
      body: [
        'We report threshold and suspicious transactions to AUSTRAC as required. We are not permitted ' +
          'to tell you when a suspicious matter report has been made.',
      ],
      draft: true,
    },
    {
      heading: '5. Record keeping',
      body: [
        'We keep identity and transaction records for seven years after your account closes, as ' +
          'required by the AML/CTF Act.',
      ],
    },
    {
      heading: '6. Your obligations',
      body: [
        'Keep your details current, use only your own payment accounts, and never let anyone else ' +
          'use your account. Providing false identity information is a criminal offence.',
      ],
    },
  ],
};

const RESPONSIBLE_PLAY: LegalDoc = {
  slug: 'responsible-play',
  title: 'Responsible Play',
  sub: 'Tools, limits, and external help if betting stops being fun.',
  eyebrow: 'Player protection',
  hero:
    'Puntsave is built so that the money you would otherwise lose is kept rather than lost. That ' +
    'reduces the financial harm of betting — it does not remove the behavioural harm. If betting ' +
    'has stopped being fun, the tools below are yours and the help below is free.',
  sections: [
    {
      heading: '1. What this product does and does not do',
      body: [
        'Losing bets move your stake into locked Savings instead of taking it from you. Winning bets ' +
          'unlock Savings into your withdrawable Play Balance. Your money moves between two buckets ' +
          'you own.',
        'What that does not change: the time you spend, the habit you build, and the way betting can ' +
          'affect your mood, sleep and relationships. The transaction fee is also a real cost, and it ' +
          'grows with how much you bet.',
      ],
    },
    {
      heading: '2. The limits you can set',
      body: [
        'From your account settings you can set:',
      ],
      list: [
        'A deposit limit — a cap on how much you can add over a period you choose.',
        'A loss limit — a cap on your net loss over a rolling window you choose. Fees count towards ' +
          'it, and an open bet counts in full until it settles, so the limit cannot be walked past by ' +
          'placing bets quickly.',
        'A cool-off — a short break during which you cannot bet.',
        'Self-exclusion — a longer break, with a minimum term.',
      ],
    },
    {
      heading: '3. How limit changes take effect',
      body: [
        'Making a limit tighter takes effect immediately. Making a limit looser, or removing it, ' +
          'takes effect only after a waiting period — so a decision made in a calm moment cannot be ' +
          'undone in an impulsive one.',
        'A cool-off or self-exclusion cannot be shortened, cancelled or reversed once it has started. ' +
          'That is the entire point of it. Open bets are left to settle normally.',
        'The available periods, the waiting period, and the minimum self-exclusion term are shown to ' +
          'you in the app at the moment you set a limit. We publish them there rather than here so ' +
          'that what you read is always what the system will actually do.',
      ],
    },
    {
      heading: '4. Reality checks and your own numbers',
      body: [
        'Your activity statement shows every deposit, bet, settlement and fee, and your running total ' +
          'of fees paid. We suggest reading it monthly. If the fee total surprises you, that is worth ' +
          'paying attention to.',
      ],
    },
    {
      heading: '5. Free help, independent of us',
      body: ['These services are free, confidential and available to anyone in Australia:'],
      list: [
        'Gambling Help Online — 1800 858 858, 24 hours a day, gamblinghelponline.org.au',
        'Lifeline — 13 11 14, 24 hours a day, for crisis support',
        'Beyond Blue — 1300 22 4636, for anxiety and depression support',
        'Financial Counselling Australia — 1800 007 007, the National Debt Helpline, for free ' +
          'financial counselling',
      ],
    },
    {
      heading: '6. If someone else is affected',
      body: [
        'Gambling Help Online supports family and friends, not just the person betting. If you are ' +
          'worried about someone, you can call on your own behalf.',
      ],
    },
    {
      heading: '7. Under 18s',
      body: [
        'Puntsave is for adults. If a person under 18 has used your account, contact us immediately ' +
          'at support@puntsave.com.au. Use device-level parental controls to keep the app off shared ' +
          'devices.',
      ],
    },
  ],
};

const COOKIES: LegalDoc = {
  slug: 'cookies',
  title: 'Cookies & Local Storage',
  sub: 'What we store in your browser and why.',
  eyebrow: 'Legal',
  hero:
    'We store the minimum needed to keep you signed in and to remember your preferences. We do not ' +
    'use advertising or cross-site tracking cookies.',
  sections: [
    {
      heading: '1. What we store',
      body: ['In your browser we store:'],
      list: [
        'Your sign-in tokens, so you stay signed in between visits. Clearing them signs you out.',
        'Your display preferences, such as light or dark theme.',
        'Short-lived technical values needed for a page to work, such as an in-progress form.',
      ],
    },
    {
      heading: '2. What we do not store',
      body: [
        'No advertising identifiers, no third-party tracking pixels, and no cross-site profiling. We ' +
          'do not sell or share browsing data.',
      ],
    },
    {
      heading: '3. Clearing it',
      body: [
        'You can clear this data at any time through your browser settings, or by signing out. ' +
          'Clearing it does not affect your account, your balances or your bet history — all of that ' +
          'lives on our servers, not in your browser.',
      ],
    },
    {
      heading: '4. Analytics',
      body: [
        'Where we measure product usage we do so in aggregate and without building a profile of an ' +
          'individual customer.',
      ],
      draft: true,
    },
  ],
};

const REFUNDS: LegalDoc = {
  slug: 'refunds',
  title: 'Refunds & Withdrawals',
  sub: 'Getting your money out, and when a deposit can be refunded.',
  eyebrow: 'Legal',
  hero:
    'You can request a withdrawal up to your withdrawable cap at any time. There is a 24-hour ' +
    'cooling-off window during which you can cancel it with a single tap — a deliberate pause ' +
    'between deciding and receiving.',
  sections: [
    {
      heading: '1. How much you can withdraw',
      body: [
        'Your withdrawable cap is your total deposits, minus the transaction fees you have paid, ' +
          'minus prior withdrawals and any withdrawal already pending.',
        'Money sitting in locked Savings is not withdrawable until it unlocks — either at the end of ' +
          'its lock term, or when a winning bet unlocks it into your Play Balance.',
      ],
    },
    {
      heading: '2. The cooling-off window',
      body: [
        'A withdrawal sits in a 24-hour cooling-off window before it is paid. During that window you ' +
          'can cancel it and the money returns to your Play Balance immediately. After the window ' +
          'closes the payment is sent and cannot be recalled by us.',
      ],
    },
    {
      heading: '3. Where the money goes',
      body: [
        'Withdrawals are paid through Zepto to a bank account in your own name. We do not pay third ' +
          'parties. If your identity verification has lapsed, payouts are paused until it is current ' +
          'again.',
        'Payment normally arrives within one business day of the cooling-off window closing.',
      ],
    },
    {
      heading: '4. Failed payouts',
      body: [
        'If a payout fails — a closed account, wrong details — the money returns to your Play ' +
          'Balance and we contact you. There is no fee for a failed payout.',
      ],
    },
    {
      heading: '5. Deposit refunds',
      body: [
        'Deposits are not generally refundable, because the money remains yours in your own account ' +
          'and can be withdrawn instead.',
        'We will refund a deposit where it was made in error, was unauthorised, or was the result of ' +
          'fraud. Contact support@puntsave.com.au within 7 days of the deposit.',
      ],
    },
    {
      heading: '6. Fees',
      body: [
        'The 4% transaction fee on a bet is capped at $20 per calendar month. It is not refunded when ' +
          'the bet loses, is voided, or is settled again after a correction, except that an ' +
          'abandoned-market void returns the stake and the fee together. There is no fee to deposit ' +
          'or to withdraw.',
      ],
    },
    {
      heading: '7. Account closure',
      body: [
        'On closure we pay out your withdrawable balance to your verified bank account. Locked ' +
          'Savings is released at the end of its term, or earlier by agreement with us.',
      ],
      draft: true,
    },
  ],
};

const COMPLAINTS: LegalDoc = {
  slug: 'complaints',
  title: 'Complaints & Disputes',
  sub: 'How to raise a problem, what happens next, and where to go if we cannot resolve it.',
  eyebrow: 'Legal',
  hero:
    'If something has gone wrong, tell us. Complaints are free, and escalating one internally or ' +
    'externally never costs you anything.',
  sections: [
    {
      heading: '1. How to complain',
      body: [
        'Use the complaints form in the app, email complaints@puntsave.com.au, or write to us at our ' +
          'registered address. Include your account email and what outcome you are looking for.',
      ],
      draft: true,
    },
    {
      heading: '2. What happens, and when',
      body: ['Our commitments:'],
      list: [
        'We acknowledge your complaint within 1 business day and give you a reference number.',
        'A complaints officer reviews it within 5 business days.',
        'We give you a substantive response within 30 calendar days.',
      ],
    },
    {
      heading: '3. Escalating inside Puntsave',
      body: [
        'If you are not satisfied with the response, you can ask for it to be escalated to our Head ' +
          'of Compliance. That review adds up to 21 days and costs nothing.',
      ],
    },
    {
      heading: '4. Escalating outside Puntsave',
      body: [
        'You do not have to accept our answer. These bodies are independent of us and free to use:',
      ],
      list: [
        'Australian Financial Complaints Authority — afca.org.au, 1800 931 678, for complaints about ' +
          'payments and financial handling.',
        'Office of the Australian Information Commissioner — oaic.gov.au, 1300 363 992, for privacy ' +
          'complaints.',
        'AUSTRAC — austrac.gov.au, for anti-money-laundering concerns.',
        'The ACCC and your state or territory consumer affairs office, for consumer law complaints.',
      ],
    },
    {
      heading: '5. If you need adjustments',
      body: [
        'Tell us if you are in financial hardship, are affected by family violence, or need us to ' +
          'communicate differently. We will adjust how we handle your complaint, and this does not ' +
          'affect the outcome.',
      ],
    },
  ],
};

const HOW_WE_MAKE_MONEY: LegalDoc = {
  slug: 'how-we-make-money',
  title: 'How Puntsave makes money',
  sub: 'One fee, stated plainly, and nothing hidden behind it.',
  eyebrow: 'Transparency',
  hero:
    'Puntsave makes money one way: a flat 4% transaction fee on each real-money bet, charged on top ' +
    'of your stake at the moment you place it and capped at $20 per calendar month. A $20 bet costs ' +
    '$20.80; no matter how much you play, your fees never exceed $20 in a month, and if you do not ' +
    'play you pay nothing. We do not profit when you lose a bet — a losing bet moves your stake into ' +
    'your own locked Savings.',
  sections: [
    {
      heading: '1. In two paragraphs',
      body: [
        'You deposit real money. Your first deposit is split between locked Savings and your Play ' +
          'Balance. You bet from your Play Balance. If you win, the payout lands in Play Balance and ' +
          'an equal amount of profit unlocks out of Savings, so more of your money becomes ' +
          'withdrawable. If you lose, the stake moves into Savings rather than being lost.',
        'Across every bet you ever place, the total real money we hold for you is unchanged by the ' +
          'results. It only goes down when you pay the transaction fee or make a withdrawal. That is ' +
          'the whole product.',
      ],
    },
    {
      heading: '2. The two balances',
      body: ['Everything sits in one of two buckets, and both are yours:'],
      list: [
        'Savings (locked) — grows when a bet loses, shrinks when a bet wins. Unlocks at the end of ' +
          'its lock term, or earlier when a win unlocks it.',
        'Play Balance — what you can bet with and withdraw. Grows on wins and on deposits, shrinks ' +
          'on stakes, fees and withdrawals.',
      ],
    },
    {
      heading: '3. What a bet actually does',
      body: [
        'Place a $20 bet at odds of 3.00 from a Play Balance of $100, with $300 in Savings. You pay ' +
          '$20 stake plus $0.80 fee, leaving $79.20.',
        'If it wins: $60 payout lands in Play Balance, taking it to $139.20. Your $40 profit unlocks ' +
          'out of Savings, taking it to $260. Same total, more of it withdrawable.',
        'If it loses: the $20 stake moves into Savings, taking it to $320. Play Balance stays at ' +
          '$79.20. You are down only the $0.80 fee.',
      ],
    },
    {
      heading: '4. What you can withdraw',
      body: [
        'Your withdrawable cap is deposits, minus fees paid, minus prior withdrawals. Winning does ' +
          'not raise the cap, because a win moves your own money rather than adding new money.',
        'If a payout is bigger than everything in Savings, your Play Balance still receives the full ' +
          'payout and Savings simply unlocks to zero. The excess is real to bet with but is not ' +
          'withdrawable, because it was never money you deposited.',
      ],
    },
    {
      heading: '5. What we do not do',
      body: ['To be explicit:'],
      list: [
        'We do not take your losses. A losing stake goes into your own Savings.',
        'We do not charge a monthly or subscription fee.',
        'We do not charge to deposit or to withdraw.',
        'We do not take a margin out of the odds you are shown beyond the published fee.',
        'We do not sell your personal information, and we do not run advertising against it.',
      ],
    },
    {
      heading: '6. Where the fee is shown',
      body: [
        'The fee is added to your stake on the bet slip before you confirm, and your running total of ' +
          'fees paid appears on your Savings page and on your activity statement. It is never ' +
          'refunded — not on a loss, not on a void, not on a corrected settlement.',
      ],
    },
  ],
};

/** Every legal document, in the order they appear on the index and in the footer. */
export const LEGAL_DOCS: readonly LegalDoc[] = [
  TERMS,
  PRIVACY,
  AML,
  RESPONSIBLE_PLAY,
  COOKIES,
  REFUNDS,
  COMPLAINTS,
  HOW_WE_MAKE_MONEY,
];

/**
 * Look a document up by its URL slug. Returns undefined for an unknown slug so the
 * route can render a not-found panel rather than throwing — an unrecognised legal URL
 * is most likely an old bookmark, not a bug worth crashing the page for.
 */
export function findLegalDoc(slug: string | undefined): LegalDoc | undefined {
  return LEGAL_DOCS.find((d) => d.slug === slug);
}

/** Path for a legal document. Single source of truth for links and routes alike. */
export function legalPath(slug: LegalSlug): string {
  return `/legal/${slug}`;
}
